import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ChevronRight, ChevronLeft, Building2, Armchair, Store, Info, Search } from "lucide-react";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import GeoHeatMap from "@/components/GeoHeatMap";

function StatTile({ testid, title, sub, onClick, reconciled, flagged, leakage, money, green, red, icon: Icon, index }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.02, 0.3) }}
      onClick={onClick} data-testid={testid}
      className="surface-card p-5 text-left cursor-pointer transition-all duration-200 hover:border-[#38BDF8] group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium truncate group-hover:text-[#38BDF8] transition-colors duration-150">
            <Icon className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-[#38BDF8]" /> {title}
          </div>
          <div className="mt-0.5 text-xs text-slate-400">{sub}</div>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-[#38BDF8] transition-colors duration-150 shrink-0" />
      </div>
      <div className="mt-4 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Captured</div>
          <div className="font-display text-xl font-light" style={{ color: green }}>{money(reconciled)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Unaccounted</div>
          <div className="font-display text-lg font-light" style={{ color: red }}>{money(leakage)}</div>
          <div className="text-[11px] text-slate-500">{flagged} flags</div>
        </div>
      </div>
    </motion.button>
  );
}

export default function Locations() {
  const { money, moneyFor, branding } = useBranding();
  const navigate = useNavigate();
  const green = branding.accent_color || "#10B981";
  const red = branding.primary_color;
  const target = branding.target || "sportclips";
  const targetMeta = ({
    sportclips: { label: "Sport Clips", foot: "~1,900 US & ~35 Canada locations" },
    chatters: { label: "Chatters", foot: "~116 Canadian salons (~84 corporate, ~32 franchise)" },
    newzealand: { label: "New Zealand", foot: "~60 stores across NZ's 5 largest hair & barber franchise brands" },
    justcuts: { label: "Just Cuts", foot: "Australian base (A$) + the Ontario launch cohort — one Just Cuts network" },
  })[target] || { label: "Sport Clips", foot: "~1,900 US & ~35 Canada locations" };

  const { data, isLoading } = useQuery({ queryKey: ["geography"], queryFn: () => api.get("/geography").then((r) => r.data) });
  const [countryCode, setCountryCode] = useState(null);
  const [stateName, setStateName] = useState(null);
  const [cityName, setCityName] = useState(null);
  const [heat, setHeat] = useState("win");
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(false);

  const countries = data?.countries || [];
  const country = countries.find((c) => c.code === countryCode) || countries[0];
  // Just Cuts spans A$ (Australia) + C$ (Ontario); show each country in its own currency.
  const countryMoney = (n) => (target === "justcuts" ? moneyFor(n, country?.code) : money(n));
  const state = (country?.states || []).find((s) => s.state === stateName) || null;
  const city = (state?.cities || []).find((c) => c.city === cityName) || null;

  const searchIndex = useMemo(() => {
    const items = [];
    countries.forEach((c) => (c.states || []).forEach((s) => (s.cities || []).forEach((ci) => {
      items.push({ type: "city", key: `city-${c.code}-${s.state}-${ci.city}`, title: ci.city,
        sub: `${s.state} · ${ci.stores} store${ci.stores !== 1 ? "s" : ""}`, country: c.code, state: s.state, city: ci.city });
      (ci.store_list || []).forEach((st) => items.push({ type: "store", key: `store-${st.number}`,
        title: `#${st.number} ${st.name}`, sub: `${ci.city}, ${s.state}`, number: st.number,
        country: c.code, state: s.state, city: ci.city }));
    })));
    return items;
  }, [countries]);

  const pickCountry = (code) => { setCountryCode(code); setStateName(null); setCityName(null); };
  const openStore = (number) => navigate(`/floor?store=${number}&from=locations`);

  if (isLoading || !country) return <div className="text-slate-400">Loading the network map…</div>;

  const level = !stateName ? "states" : !cityName ? "cities" : "stores";

  const query = q.trim().toLowerCase();
  const results = query
    ? searchIndex
        .filter((it) =>
          it.title.toLowerCase().includes(query) ||
          (it.number && it.number.toLowerCase().includes(query)) ||
          it.city.toLowerCase().includes(query) ||
          it.state.toLowerCase().includes(query))
        .sort((a, b) => {
          const an = a.number && a.number.toLowerCase() === query ? 0 : 1;
          const bn = b.number && b.number.toLowerCase() === query ? 0 : 1;
          if (an !== bn) return an - bn;
          return (a.type === "store" ? 0 : 1) - (b.type === "store" ? 0 : 1);
        })
        .slice(0, 8)
    : [];

  const pick = (it) => {
    if (it.type === "store") { openStore(it.number); }
    else { setCountryCode(it.country); setStateName(it.state); setCityName(it.city); }
    setQ(""); setFocus(false);
  };

  const Crumb = ({ label, onClick, active }) => (
    <button onClick={onClick} disabled={active} data-testid={`loc-crumb-${label}`}
      className={`text-sm ${active ? "text-white font-medium" : "text-slate-400 hover:text-[#38BDF8]"} transition-colors duration-150`}>
      {label}
    </button>
  );

  return (
    <div data-testid="locations-page">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Locations · Browse by geography</div>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">
        {data.total_stores} stores across the network
      </h1>

      {/* Simulation disclaimer */}
      <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs text-amber-200/90" data-testid="locations-disclaimer">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span><strong>Illustrative simulation for {targetMeta.label} head-office review.</strong> Store locations, counts and daily activity are generated to demonstrate the platform against a {targetMeta.foot} footprint — not the company's real records. Activity re-generates every 7 days.</span>
      </div>

      {/* Search: jump to a city or store number */}
      <div className="mt-6 relative max-w-xl" data-testid="loc-search">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setFocus(true); }}
            onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 150)}
            onKeyDown={(e) => { if (e.key === "Enter" && results[0]) pick(results[0]); }}
            placeholder="Jump to a city or store number — e.g. Dallas or TX131…"
            data-testid="loc-search-input"
            className="w-full rounded-lg bg-[#1E2638] border border-[#2A3550] pl-9 pr-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#38BDF8]" />
        </div>
        {focus && results.length > 0 && (
          <div className="absolute z-30 mt-1 w-full rounded-lg border border-[#2A3550] bg-[#171E2E] shadow-xl overflow-hidden" data-testid="loc-search-results">
            {results.map((it) => (
              <button key={it.key} onMouseDown={() => pick(it)} data-testid={`loc-search-${it.type}-${it.number || it.city}`}
                className="w-full text-left px-4 py-2.5 hover:bg-[#1E2638] transition-colors duration-150 flex items-center gap-3 border-b border-[#232C40] last:border-0">
                {it.type === "store" ? <Store className="h-4 w-4 text-slate-400 shrink-0" /> : <Building2 className="h-4 w-4 text-slate-400 shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm text-slate-100 truncate">{it.title}</div>
                  <div className="text-xs text-slate-400 truncate">{it.sub}</div>
                </div>
                <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-500">{it.type}</span>
              </button>
            ))}
          </div>
        )}
        {focus && query && results.length === 0 && (
          <div className="absolute z-30 mt-1 w-full rounded-lg border border-[#2A3550] bg-[#171E2E] px-4 py-3 text-sm text-slate-400" data-testid="loc-search-empty">
            No city or store matches “{q}”.
          </div>
        )}
      </div>

      {/* Country tabs */}
      {countries.length > 1 && (
        <div className="mt-6 inline-flex rounded-lg border border-[#2A3550] bg-[#1E2638] p-1">
          {countries.map((c) => (
            <button key={c.code} onClick={() => pickCountry(c.code)} data-testid={`country-tab-${c.code}`}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors duration-200 ${country.code === c.code ? "bg-[#171E2E] text-white" : "text-slate-400 hover:text-white"}`}>
              {c.country} <span className="text-slate-500">· {c.stores}</span>
            </button>
          ))}
        </div>
      )}

      {/* Breadcrumb */}
      <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="loc-breadcrumb">
        <Crumb label={country.country} onClick={() => { setStateName(null); setCityName(null); }} active={level === "states"} />
        {stateName && <><ChevronRight className="h-3.5 w-3.5 text-slate-600" /><Crumb label={stateName} onClick={() => setCityName(null)} active={level === "cities"} /></>}
        {cityName && <><ChevronRight className="h-3.5 w-3.5 text-slate-600" /><Crumb label={cityName} active={level === "stores"} /></>}
      </div>

      {(stateName || cityName) && (
        <button onClick={() => (cityName ? setCityName(null) : setStateName(null))} data-testid="loc-back"
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#2A3550] bg-[#1E2638] px-3 py-1.5 text-xs text-slate-300 hover:border-[#38BDF8] transition-colors duration-200">
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>
      )}

      {/* States: heatmap + ranked grid */}
      {level === "states" && (
        <>
          {["US", "CA", "AU", "NZ"].includes(country.code) && (
          <div className="mt-6 surface-card p-6">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" style={{ color: heat === "win" ? green : red }} />
                <h2 className="font-display text-lg font-medium">
                  {country.country} — {heat === "win" ? "matched-sales" : "potential-leakage"} heatmap
                </h2>
              </div>
              <div className="inline-flex rounded-lg border border-[#2A3550] bg-[#1E2638] p-1" data-testid="heat-toggle">
                <button onClick={() => setHeat("leak")} data-testid="heat-toggle-leak"
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors duration-200 ${heat === "leak" ? "text-white" : "text-slate-400 hover:text-white"}`}
                  style={heat === "leak" ? { backgroundColor: red } : {}}>
                  Potential leakage
                </button>
                <button onClick={() => setHeat("win")} data-testid="heat-toggle-win"
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors duration-200 ${heat === "win" ? "text-white" : "text-slate-400 hover:text-white"}`}
                  style={heat === "win" ? { backgroundColor: green } : {}}>
                  Matched sales
                </button>
              </div>
            </div>
            <GeoHeatMap country={country.code} states={country.states} money={countryMoney}
              metric={heat} hot={heat === "win" ? green : red} cool={heat === "win" ? "#0F2E22" : "#14324F"}
              onSelect={(name) => { setStateName(name); setCityName(null); }} />
          </div>
          )}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {country.states.map((s, i) => (
              <StatTile key={s.state} index={i} testid={`state-tile-${s.code || s.state}`}
                title={s.state} sub={`${s.stores} store${s.stores !== 1 ? "s" : ""} · ${s.region}`}
                reconciled={s.reconciled} leakage={s.leakage} flagged={s.flagged}
                money={countryMoney} green={green} red={red} icon={MapPin}
                onClick={() => { setStateName(s.state); setCityName(null); }} />
            ))}
          </div>
        </>
      )}

      {/* Cities grid */}
      {level === "cities" && state && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {state.cities.map((c, i) => (
            <StatTile key={c.city} index={i} testid={`city-tile-${c.city}`}
              title={c.city} sub={`${c.stores} store${c.stores !== 1 ? "s" : ""}`}
              reconciled={c.reconciled} leakage={c.leakage} flagged={c.flagged}
              money={countryMoney} green={green} red={red} icon={Building2}
              onClick={() => setCityName(c.city)} />
          ))}
        </div>
      )}

      {/* Store list */}
      {level === "stores" && city && (
        <div className="mt-6 surface-card p-6">
          <h2 className="font-display text-lg font-medium mb-4">{city.city}, {stateName} · {city.stores} store{city.stores !== 1 ? "s" : ""}</h2>
          <div className="space-y-1.5" data-testid="loc-store-list">
            {city.store_list.map((s) => (
              <div key={s.store_id} onClick={() => openStore(s.number)} role="button" tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openStore(s.number)}
                title="Open this store's live floor" data-testid={`loc-store-${s.number}`}
                className="flex items-center gap-4 cursor-pointer rounded-lg -mx-2 px-3 py-2.5 hover:bg-[#171E2E] transition-colors duration-150 group border-b border-[#232C40] last:border-0">
                <Store className="h-4 w-4 text-slate-500 group-hover:text-[#38BDF8] transition-colors duration-150 shrink-0" />
                <div className="w-56 shrink-0">
                  <div className="text-sm font-medium group-hover:text-[#38BDF8] transition-colors duration-150">#{s.number} {s.name}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-1"><Armchair className="h-3 w-3" /> {s.chairs} chairs · {s.match_rate}% match</div>
                </div>
                <div className="flex-1 hidden sm:flex items-center gap-6 text-sm">
                  <div><span className="text-slate-500 text-xs">Captured </span><span style={{ color: green }}>{countryMoney(s.reconciled)}</span></div>
                  <div><span className="text-slate-500 text-xs">Unaccounted </span><span style={{ color: red }}>{countryMoney(s.leakage)}</span></div>
                  <div className="text-slate-400 text-xs">{s.flagged} flags</div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-[#38BDF8] transition-colors duration-150 shrink-0 ml-auto" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
